import React, { useState, useEffect } from "react";
import { Shield, Key, HeartPulse, Info, ClipboardList, Users, GitBranch, BookOpen, Workflow } from "lucide-react";
import UserManagement from "./UserManagement";
import WorkflowAdminPage from "../pages/WorkflowAdminPage";
import RoleManagement from "./RoleManagement";
import PersonaManagement from "./PersonaManagements";
import QuestionnaireManagement from "./QuestionnaireManagement";
import GuidelinesManagement from "./GuidelinesManagement";
import SecurityManagement from "./Security";
import SignInLogs from "./SignInLogs";
import AuditLogs from "./AuditLogs";
import SystemHealth from "./SystemHealth";
import SystemInformation from "./SystemInformation";
import MFAManagement from "./MFAManagement";
import DataLineage from "./DataLineage";

const Admin = ({ activeTab, setActiveTab }) => {
  const [userCount, setUserCount] = useState(0);
  const [roleCount, setRoleCount] = useState(0);
  const [personaCount, setPersonaCount] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);

  // Fetch counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch user count
        const userResponse = await fetch('/api/user_management/user_data');
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUserCount(userData.length);
        }

        // Fetch role count
        const roleResponse = await fetch('/api/role_management/get_role');
        if (roleResponse.ok) {
          const roleData = await roleResponse.json();
          const roles = JSON.parse(roleData.roles);
          setRoleCount(roles.length);
        }

        // Fetch persona count
        const personaResponse = await fetch('/api/persona_management/get_persona');
        if (personaResponse.ok) {
          const personaData = await personaResponse.json();
          const personas = JSON.parse(personaData.persona);
          setPersonaCount(personas.length);
        }

        // Fetch question count
        const questionResponse = await fetch('/api/question/get_questions');
        if (questionResponse.ok) {
          const questionData = await questionResponse.json();
          setQuestionCount(questionData.length);
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchCounts();
  }, []);

  // Explicit check for admin root route
  const isAdminRoot = () => {
    return activeTab === 'admin' ||
           activeTab === undefined ||
           activeTab === null ||
           activeTab === '';
  };

  const adminCards = [
    {
      id: 'admin-workflows',
      title: 'Workflows',
      description: 'Design how ideas, demands and fulfilment are routed, and who owns each step',
      icon: Workflow,
      color: 'var(--cares-purple)',
      bgColor: '#eef2ff'
    },
    {
      id: 'admin-users',
      title: 'User Management',
      description: 'Manage user accounts, permissions, and access control',
      icon: Users,
      color: 'var(--cares-blue)',
      bgColor: '#eff6ff',
      count: userCount
    },
    {
      id: 'admin-roles',
      title: 'Role Management',
      description: 'Define and manage user roles and permissions',
      icon: Key,
      color: 'var(--cares-purple)',
      bgColor: '#f5f3ff',
      count: roleCount
    },
    {
      id: 'admin-persona-management',
      title: 'Persona Management',
      description: 'Configure user personas and governance areas',
      icon: Users,
      color: 'var(--cares-teal)',
      bgColor: '#ecfeff',
      count: personaCount
    },
    {
      id: 'admin-questionnaire',
      title: 'Questionnaire Management',
      description: 'Create and manage assessment questionnaires',
      icon: ClipboardList,
      color: 'var(--cares-green)',
      bgColor: '#ecfdf5',
      count: questionCount
    },
    {
      id: 'admin-guidelines',
      title: 'Guidelines & Rules Engine',
      description: 'Manage review criteria, constraints, risks, and goals',
      icon: BookOpen,
      color: 'var(--cares-purple)',
      bgColor: '#f5f3ff'
    },
    {
      id: 'admin-mfa',
      title: 'MFA Authentication',
      description: 'Configure multi-factor authentication settings',
      icon: Shield,
      color: 'var(--cares-amber)',
      bgColor: '#fffbeb'
    },
    {
      id: 'admin-data-lineage',
      title: 'Data Lineage',
      description: 'Track all changes to users, roles, personas, and questions',
      icon: GitBranch,
      color: 'var(--cares-blue)',
      bgColor: '#dbeafe'
    },
    {
      id: 'admin-signin-logs',
      title: 'Sign-in Logs',
      description: 'View and monitor user sign-in activity',
      icon: ClipboardList,
      color: 'var(--cares-orange)',
      bgColor: '#fef2f2'
    },
    {
      id: 'admin-audit-logs',
      title: 'Audit Logs',
      description: 'Track system changes and administrative actions',
      icon: ClipboardList,
      color: 'var(--cares-magenta)',
      bgColor: '#fdf2f8'
    },
    {
      id: 'admin-system-health',
      title: 'System Health',
      description: 'Monitor system performance and health metrics',
      icon: HeartPulse,
      color: 'var(--cares-teal)',
      bgColor: '#f0fdfa'
    },
    {
      id: 'admin-system-info',
      title: 'System Information',
      description: 'View system configuration and environment details',
      icon: Info,
      color: 'var(--cares-purple)',
      bgColor: '#eef2ff'
    }
  ];

  const getContent = () => {
    if (isAdminRoot()) {
      return (
        <div style={{ marginTop: '32px' }}>
          {/* Admin Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px'
          }}>
            {adminCards.map((card) => {
              const IconComponent = card.icon;
              return (
                <div
                  key={card.id}
                  onClick={() => setActiveTab && setActiveTab(card.id)}
                  style={{
                    background: 'var(--cares-surface-card)',
                    borderRadius: '16px',
                    padding: '24px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid var(--cares-line-card)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: card.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IconComponent style={{ width: '28px', height: '28px', color: card.color }} />
                  </div>

                  {/* Content */}
                  <div>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--cares-text-1)',
                      margin: '0 0 8px 0'
                    }}>
                      {card.title}
                      {card.count > 0 && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '16px',
                          fontWeight: '500',
                          color: card.color,
                          background: card.bgColor,
                          padding: '2px 10px',
                          borderRadius: '12px'
                        }}>
                          {card.count}
                        </span>
                      )}
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--cares-text-label)',
                      margin: 0,
                      lineHeight: '1.5'
                    }}>
                      {card.description}
                    </p>
                  </div>

                  {/* Arrow indicator */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: 'auto',
                    color: card.color,
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    <span>Manage</span>
                    <span>→</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'admin-workflows':
        return <WorkflowAdminPage />;
      case 'admin-users':
        return <UserManagement />;
      case 'admin-roles':
        return <RoleManagement />;
      case 'admin-persona-management':
        return <PersonaManagement />;
      case 'admin-questionnaire':
        return <QuestionnaireManagement />;
      case 'admin-guidelines':
        return <GuidelinesManagement />;
      case 'admin-mfa':
        return <MFAManagement />;
      case 'admin-security':
        return <SecurityManagement />;
      case 'admin-data-lineage':
        return <DataLineage />;
      case 'admin-signin-logs':
        return <SignInLogs />;
      case 'admin-audit-logs':
        return <AuditLogs />;
      case 'admin-system-health':
        return <SystemHealth />;
      case 'admin-system-info':
        return <SystemInformation />;
      default:
        return <div className="flex items-center justify-center h-64">
          <Users className="w-12 h-12 text-gray-400" />
          <p className="ml-4 text-gray-500">Select an admin function from the menu</p>
        </div>;
    }
  };

  const getBreadcrumbContent = () => {
    if (isAdminRoot()) {
      return null; // No breadcrumb for admin root
    }

    // Map of admin tabs to their display names
    const tabNames = {
      'admin-workflows': 'Workflows',
      'admin-users': 'User Management',
      'admin-roles': 'Role Management',
      'admin-persona-management': 'Persona Management',
      'admin-questionnaire': 'Questionnaire Management',
      'admin-guidelines': 'Guidelines & Rules Engine',
      'admin-mfa': 'MFA Authentication',
      'admin-security': 'Security',
      'admin-data-lineage': 'Data Lineage',
      'admin-signin-logs': 'Sign-in Logs',
      'admin-audit-logs': 'Audit Logs',
      'admin-system-health': 'System Health',
      'admin-system-info': 'System Information'
    };

    const title = tabNames[activeTab] || 'Admin';
    const breadcrumb = `Admin > ${title}`;

    return { title, breadcrumb };
  };

  const breadcrumbData = getBreadcrumbContent();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cares-surface-board)' }}>
      <div style={{ padding: '24px' }}>
        {breadcrumbData && (
          <div style={{ marginBottom: '24px', marginTop: '32px' }}>
            {/* Breadcrumb */}
            <div style={{
              fontSize: '14px',
              color: 'var(--cares-text-label)',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ color: 'var(--cares-text-note)' }}>Admin</span>
              <span style={{ color: 'var(--cares-text-note)' }}>›</span>
              <span style={{ color: 'var(--cares-text-3)', fontWeight: '500' }}>{breadcrumbData.title}</span>
            </div>
            {/* Page Title */}
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: 'var(--cares-text-1)',
              margin: 0
            }}>
              {breadcrumbData.title}
            </h1>
          </div>
        )}
        {getContent()}
      </div>
    </div>
  );
};

export default Admin;